import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  afterNextRender,
  inject,
  output,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent, InfoBannerComponent, ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformTfaService } from '../../../../services/platform-tfa.service';
import { PlatformTfaInputComponent } from '../tfa-input/platform-tfa-input.component';

@Component({
  selector: 'app-platform-add-tfa-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    InfoBannerComponent,
    TranslocoPipe,
    PlatformTfaInputComponent,
    ButtonComponent,
  ],
  templateUrl: './platform-add-tfa-modal.component.html',
  styleUrl: './platform-add-tfa-modal.component.scss',
})
export class PlatformAddTfaModalComponent {
  private readonly tfaService = inject(PlatformTfaService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  readonly methodAdded = output<void>();

  readonly newMethodModal = viewChild.required<ModalComponent>('addMethodModal');
  readonly tfaInput = viewChild<PlatformTfaInputComponent>('tfaInput');

  addMethodForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });
  addMethodFormSubmitAttempt = false;
  tfaForm: FormGroup = new FormGroup({
    id: new FormControl<number | null>(null, [Validators.required]),
    tfaCode: new FormControl('', [Validators.required]),
  });
  tfaFormSubmitAttempt = false;
  tfaQRCodeImage: string | undefined;
  tfaQRCode: string | undefined;
  qrLoaded = false;
  loadingQr = false;

  /**
   * Opens the add-method modal.
   */
  open(): void {
    this.newMethodModal().open();
  }

  /**
   * Resets the setup form when the modal opens.
   */
  onModalOpen(): void {
    this.reset();
    this.cdr.markForCheck();
  }

  /**
   * Cancels an unfinished credential when the modal closes.
   */
  onModalCloseEnd(): void {
    if (this.id.value) {
      this.tfaService.cancelAppCreation(this.id.value);
    }
    this.reset();
    this.cdr.markForCheck();
  }

  /**
   * Requests a TOTP secret after the method name is valid.
   */
  onContinue(): void {
    this.addMethodFormSubmitAttempt = true;
    if (!this.addMethodForm.valid) {
      this.cdr.markForCheck();
      return;
    }
    this.addMethodFormSubmitAttempt = false;
    this.loadingQr = true;
    this.tfaService.getAppCredential(this.name.value).subscribe({
      next: (result) => {
        this.tfaQRCode = result.code;
        this.tfaQRCodeImage = result['qr-code-image'];
        this.id.patchValue(result.id);
        this.qrLoaded = true;
        this.loadingQr = false;
        this.cdr.markForCheck();
        afterNextRender(() => this.tfaInput()?.focus(), { injector: this.injector });
      },
      error: () => {
        this.loadingQr = false;
        this.cdr.markForCheck();
      },
    });
    this.cdr.markForCheck();
  }

  /**
   * Confirms the authenticator-app setup with a TOTP code.
   * @param tfaValue - Six-digit code
   */
  onTFASubmit(tfaValue: string): void {
    this.tfaCode.patchValue(tfaValue);
    this.tfaFormSubmitAttempt = true;
    if (!this.tfaForm.valid) {
      this.cdr.markForCheck();
      return;
    }
    this.tfaFormSubmitAttempt = false;
    this.tfaService.confirmAppCredential(this.id.value, this.tfaCode.value).subscribe({
      next: () => {
        this.tfaInput()?.validate(true);
        setTimeout(() => {
          this.id.patchValue(null);
          this.newMethodModal().close();
          this.methodAdded.emit();
          this.cdr.markForCheck();
        }, 1000);
      },
      error: () => {
        this.tfaInput()?.validate(false);
      },
    });
    this.cdr.markForCheck();
  }

  /**
   * Clears setup state.
   */
  private reset(): void {
    this.addMethodForm.reset();
    this.tfaForm.reset();
    this.qrLoaded = false;
    this.loadingQr = false;
    this.tfaQRCodeImage = undefined;
    this.tfaQRCode = undefined;
    this.addMethodFormSubmitAttempt = false;
    this.tfaFormSubmitAttempt = false;
    this.tfaInput()?.reset();
  }

  get name() {
    return this.addMethodForm.get('name')!;
  }

  get tfaCode() {
    return this.tfaForm.get('tfaCode')!;
  }

  get id() {
    return this.tfaForm.get('id')!;
  }
}
